/**
 * Separation Detail — 6 tabs: Overview / Handover / No-Dues / F&F /
 * Exit Interview / Audit Log.
 *
 * Route: /hr/separations/:id
 *
 * The header carries: separation_no + status pipeline (Steps) + LWD +
 * action buttons (Approve LWD / Generate Checklists / Transition / Hold /
 * Cancel). Block-reasons surfaced as a yellow Alert at the top when set.
 */
import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Alert, Button, Card, Col, DatePicker, Descriptions, Drawer, Form, Input,
  InputNumber, Modal, Popconfirm, Progress, Row, Select, Space, Spin, Statistic,
  Steps, Table, Tabs, Tag, Tooltip, Typography, message,
} from 'antd'
import {
  ArrowLeftOutlined, CalendarOutlined, CheckCircleOutlined, DollarOutlined,
  EditOutlined, ExclamationCircleOutlined, PauseCircleOutlined,
  PlayCircleOutlined, ThunderboltOutlined, WarningOutlined,
} from '@ant-design/icons'
import dayjs from 'dayjs'

import {
  separationApi, STATUS_COLOR, TYPE_COLOR,
  DATA_SOURCE_LABEL, FACILITY_LABEL,
  type ClearanceItem, type DuesPreview, type FacilityDues,
  type HandoverItem, type SeparationStatus,
} from '@/features/hr/separationApi'

const { Text, Title, Paragraph } = Typography
const { TextArea } = Input

// Major checkpoints displayed in the stepper (collapsed view of the full
// 17-state machine to keep the UI scannable).
const STEP_FLOW: { label: string; statuses: SeparationStatus[] }[] = [
  { label: 'Draft', statuses: ['DRAFT'] },
  { label: 'Submitted', statuses: ['SUBMITTED'] },
  { label: 'HR Review', statuses: ['MANAGER_REVIEW', 'HR_REVIEW'] },
  { label: 'Notice & Handover', statuses: ['NOTICE_PERIOD_RUNNING', 'HANDOVER_PENDING'] },
  { label: 'No-Dues', statuses: ['NO_DUES_PENDING'] },
  { label: 'F&F', statuses: ['PAYROLL_PENDING', 'FINANCE_REVIEW', 'FNF_PENDING_APPROVAL', 'FNF_APPROVED'] },
  { label: 'Payment', statuses: ['PAYMENT_PENDING'] },
  { label: 'Paid', statuses: ['PAID'] },
  { label: 'Closed', statuses: ['CLOSED'] },
]

const TERMINAL = new Set<SeparationStatus>(['CLOSED', 'CANCELLED', 'REJECTED'])
const OFF_PATH = new Set<SeparationStatus>(['HOLD', 'CANCELLED', 'REJECTED'])

export default function SeparationDetailPage() {
  const { id = '' } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()

  const detailQ = useQuery({
    queryKey: ['separation-detail', id],
    queryFn: () => separationApi.detail(id),
    enabled: !!id,
  })

  const [transitionOpen, setTransitionOpen] = useState(false)
  const [transitionForm] = Form.useForm()
  const [lwdOpen, setLwdOpen] = useState(false)
  const [lwdForm] = Form.useForm()
  const [holdOpen, setHoldOpen] = useState(false)
  const [holdForm] = Form.useForm()

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['separation-detail', id] })
    qc.invalidateQueries({ queryKey: ['separations'] })
    qc.invalidateQueries({ queryKey: ['separation-summary'] })
  }

  const transitionMut = useMutation({
    mutationFn: (v: any) => separationApi.transition(id, v),
    onSuccess: () => { message.success('Status changed'); setTransitionOpen(false); refresh() },
    onError: (e: any) => message.error(e?.response?.data?.detail ?? 'Transition failed'),
  })

  const approveLWDMut = useMutation({
    mutationFn: (v: string) => separationApi.approveLWD(id, v),
    onSuccess: () => { message.success('LWD approved'); setLwdOpen(false); refresh() },
    onError: (e: any) => message.error(e?.response?.data?.detail ?? 'LWD approve failed'),
  })

  const genChecklistsMut = useMutation({
    mutationFn: () => separationApi.generateChecklists(id),
    onSuccess: (r) => {
      message.success(
        `Generated ${r.handover_items_created} handover + ${r.clearance_items_created} clearance items`,
      )
      refresh()
    },
    onError: (e: any) => message.error(e?.response?.data?.detail ?? 'Generation failed'),
  })

  const holdMut = useMutation({
    mutationFn: (v: any) => separationApi.transition(id, {
      new_status: 'HOLD', hold_reason: v.hold_reason,
    }),
    onSuccess: () => { message.success('Placed on HOLD'); setHoldOpen(false); refresh() },
    onError: (e: any) => message.error(e?.response?.data?.detail ?? 'Hold failed'),
  })

  if (detailQ.isLoading) return <div style={{ padding: 60, textAlign: 'center' }}><Spin size="large" /></div>
  if (!detailQ.data) return <Card>Separation not found</Card>

  const { request: r, handover_items, clearance_items, fnf, block_reasons } = detailQ.data

  // Where are we in the simplified pipeline?
  const offPath = OFF_PATH.has(r.current_status)
  const currentIdx = (() => {
    for (let i = 0; i < STEP_FLOW.length; i++) {
      if (STEP_FLOW[i].statuses.includes(r.current_status)) return i
    }
    return offPath ? 0 : 0
  })()
  const isTerminal = TERMINAL.has(r.current_status)
  const isPaid = r.current_status === 'PAID' || r.current_status === 'CLOSED'

  // Block reasons → derived "Why can't I advance?" panel.
  const handoverPending = handover_items.filter((it) => it.required && it.status === 'PENDING').length
  const handoverTotal = handover_items.filter((it) => it.required).length
  const clearancePending = clearance_items.filter(
    (it) => it.required && (it.clearance_status === 'PENDING' || it.clearance_status === 'HOLD'),
  ).length
  const clearanceTotal = clearance_items.filter((it) => it.required).length
  const totalRecoverable = clearance_items.reduce(
    (s, it) => s + (it.amount_recoverable ?? 0), 0,
  )

  const tabItems = [
    {
      key: 'overview',
      label: 'Overview',
      children: <OverviewTab detail={detailQ.data} refresh={refresh} />,
    },
    {
      key: 'handover',
      label: <Space size={4}>Handover {handoverTotal > 0 && (
        <Tag color={handoverPending === 0 ? 'success' : 'warning'} style={{ marginLeft: 4 }}>
          {handoverTotal - handoverPending}/{handoverTotal}
        </Tag>
      )}</Space>,
      children: <HandoverTab items={handover_items} refresh={refresh} />,
    },
    {
      key: 'no-dues',
      label: <Space size={4}>No-Dues {clearanceTotal > 0 && (
        <Tag color={clearancePending === 0 ? 'success' : 'warning'} style={{ marginLeft: 4 }}>
          {clearanceTotal - clearancePending}/{clearanceTotal}
        </Tag>
      )}</Space>,
      children: <ClearanceTab items={clearance_items} refresh={refresh} />,
    },
    {
      key: 'fnf',
      label: <Space size={4}>Full & Final {fnf && (
        <Tag color={fnf.settlement_status === 'PAID' ? 'success' : 'processing'}
          style={{ marginLeft: 4, fontSize: 10 }}>
          {fnf.settlement_status}
        </Tag>
      )}</Space>,
      children: <FNFTab fnf={fnf} separationId={id} canCalculate={!!r.approved_last_working_date}
                       refresh={refresh} />,
    },
    {
      key: 'exit-interview',
      label: 'Exit Interview',
      children: <ExitInterviewTab detail={detailQ.data} refresh={refresh} />,
    },
  ]

  return (
    <div style={{ padding: 16 }}>
      <Space style={{ marginBottom: 12 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/hr/separations')}>
          Back
        </Button>
      </Space>

      <Card size="small" style={{ marginBottom: 12 }}>
        <Row gutter={16} align="middle">
          <Col flex="auto">
            <Space direction="vertical" size={4}>
              <Space wrap>
                <h2 style={{ margin: 0 }}>{r.emp_name ?? r.employee_code}</h2>
                <Text code>{r.separation_no}</Text>
                <Tag color={TYPE_COLOR[r.separation_type]}>{r.separation_type}</Tag>
                <Tag color={STATUS_COLOR[r.current_status]}>{r.current_status.replace(/_/g, ' ')}</Tag>
                {r.is_hard_exit && <Tag color="red">hard-exit</Tag>}
                {r.approved_last_working_date && (
                  <Tag icon={<CalendarOutlined />}>
                    LWD: {dayjs(r.approved_last_working_date).format('DD MMM YYYY')}
                  </Tag>
                )}
              </Space>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {r.employee_code} · {r.department_name ?? 'no dept'} · {r.designation ?? '—'}
              </Text>
            </Space>
          </Col>
          <Col>
            <Space wrap>
              {!r.approved_last_working_date && !isTerminal && (
                <Button icon={<CalendarOutlined />} onClick={() => setLwdOpen(true)}>
                  Approve LWD
                </Button>
              )}
              {handover_items.length === 0 && clearance_items.length === 0 && !isTerminal && (
                <Button type="primary" icon={<ThunderboltOutlined />}
                  loading={genChecklistsMut.isPending}
                  onClick={() => genChecklistsMut.mutate()}>
                  Generate Checklists
                </Button>
              )}
              {!isTerminal && (
                <Button icon={<PlayCircleOutlined />}
                  onClick={() => { transitionForm.resetFields(); setTransitionOpen(true) }}>
                  Transition
                </Button>
              )}
              {!isTerminal && r.current_status !== 'HOLD' && (
                <Button danger icon={<PauseCircleOutlined />}
                  onClick={() => { holdForm.resetFields(); setHoldOpen(true) }}>
                  Hold
                </Button>
              )}
            </Space>
          </Col>
        </Row>

        <div style={{ marginTop: 16 }}>
          <Steps
            current={offPath ? 0 : currentIdx}
            size="small"
            status={offPath ? 'error' : isPaid ? 'finish' : 'process'}
            items={STEP_FLOW.map((s) => ({ title: s.label }))}
          />
          {offPath && (
            <Alert
              type={r.current_status === 'HOLD' ? 'warning' : 'error'}
              showIcon
              style={{ marginTop: 8 }}
              message={`${r.current_status} — off the main path`}
              description={r.hold_reason || r.override_reason || 'No reason recorded'}
            />
          )}
        </div>
      </Card>

      {block_reasons.length > 0 && (
        <Alert
          type="warning"
          showIcon
          icon={<WarningOutlined />}
          style={{ marginBottom: 12 }}
          message={`${block_reasons.length} thing(s) blocking next step`}
          description={
            <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
              {block_reasons.map((b, i) => <li key={i}>{b}</li>)}
            </ul>
          }
        />
      )}

      {totalRecoverable > 0 && (
        <Alert
          type="info"
          showIcon
          icon={<DollarOutlined />}
          style={{ marginBottom: 12 }}
          message={`₹${totalRecoverable.toFixed(2)} in recoveries pending — these will deduct from F&F net payable`}
        />
      )}

      <Card size="small">
        <Tabs items={tabItems} />
      </Card>

      {/* Transition modal */}
      <Modal
        open={transitionOpen}
        title="Move to next status"
        onCancel={() => setTransitionOpen(false)}
        onOk={() => transitionForm.validateFields().then((v) => transitionMut.mutate(v))}
        confirmLoading={transitionMut.isPending}
      >
        <Form form={transitionForm} layout="vertical">
          <Form.Item name="new_status" label="New Status"
            rules={[{ required: true, message: 'Pick a status' }]}>
            <Select
              options={['SUBMITTED', 'MANAGER_REVIEW', 'HR_REVIEW',
                'NOTICE_PERIOD_RUNNING', 'HANDOVER_PENDING', 'NO_DUES_PENDING',
                'PAYROLL_PENDING', 'FINANCE_REVIEW', 'FNF_PENDING_APPROVAL',
                'FNF_APPROVED', 'PAYMENT_PENDING', 'PAID', 'CLOSED',
                'CANCELLED', 'REJECTED'].map((s) => ({ label: s.replace(/_/g, ' '), value: s }))}
            />
          </Form.Item>
          <Form.Item
            name="override_reason"
            label="Override reason (only needed for invalid transitions)"
            tooltip="Use only when the state machine refuses a forward jump. Audit-logged."
          >
            <TextArea rows={2} placeholder="e.g. retroactive correction approved by GM" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Approve LWD modal */}
      <Modal
        open={lwdOpen}
        title="Approve Last Working Date"
        onCancel={() => setLwdOpen(false)}
        onOk={() => lwdForm.validateFields().then((v) =>
          approveLWDMut.mutate(dayjs(v.lwd).format('YYYY-MM-DD'))
        )}
        confirmLoading={approveLWDMut.isPending}
      >
        <Form form={lwdForm} layout="vertical">
          <Form.Item name="lwd" label="Approved LWD"
            rules={[{ required: true, message: 'Pick a date' }]}>
            <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" />
          </Form.Item>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Notice shortfall will auto-calculate vs the resignation date.
            For hard-exits, this just records the actual last day on premises.
          </Text>
        </Form>
      </Modal>

      {/* Hold modal */}
      <Modal
        open={holdOpen}
        title="Place separation on HOLD"
        onCancel={() => setHoldOpen(false)}
        onOk={() => holdForm.validateFields().then((v) => holdMut.mutate(v))}
        confirmLoading={holdMut.isPending}
        okButtonProps={{ danger: true }}
      >
        <Form form={holdForm} layout="vertical">
          <Form.Item name="hold_reason" label="Reason"
            rules={[{ required: true, message: 'Capture why' }]}>
            <TextArea rows={3} placeholder="e.g. Employee disputing notice recovery — pending mgmt review" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}


// ─── Overview Tab ─────────────────────────────────────────────────

function OverviewTab({ detail }: { detail: any; refresh: () => void }) {
  const r = detail.request
  return (
    <Descriptions bordered size="small" column={2}>
      <Descriptions.Item label="Separation Type">{r.separation_type}</Descriptions.Item>
      <Descriptions.Item label="Hard Exit">{r.is_hard_exit ? 'Yes' : 'No'}</Descriptions.Item>
      <Descriptions.Item label="Resignation Date">
        {r.resignation_date ? dayjs(r.resignation_date).format('DD MMM YYYY') : '—'}
      </Descriptions.Item>
      <Descriptions.Item label="Requested LWD">
        {r.requested_last_working_date ? dayjs(r.requested_last_working_date).format('DD MMM YYYY') : '—'}
      </Descriptions.Item>
      <Descriptions.Item label="Approved LWD">
        {r.approved_last_working_date
          ? <Text strong>{dayjs(r.approved_last_working_date).format('DD MMM YYYY')}</Text>
          : <Text type="secondary">Not yet approved</Text>}
      </Descriptions.Item>
      <Descriptions.Item label="Notice Required">
        {r.notice_period_days_required} days
      </Descriptions.Item>
      <Descriptions.Item label="Notice Served">
        {r.notice_period_days_served != null ? `${r.notice_period_days_served} days` : '—'}
      </Descriptions.Item>
      <Descriptions.Item label="Notice Shortfall">
        {r.notice_shortfall_days != null
          ? <Text type={r.notice_shortfall_days > 0 ? 'danger' : undefined}>
              {r.notice_shortfall_days} days
            </Text>
          : '—'}
      </Descriptions.Item>
      <Descriptions.Item label="Reason for Leaving" span={2}>
        {r.reason_for_leaving ?? <Text type="secondary">—</Text>}
      </Descriptions.Item>
      <Descriptions.Item label="Detailed Remarks" span={2}>
        {r.detailed_remarks ?? <Text type="secondary">—</Text>}
      </Descriptions.Item>
    </Descriptions>
  )
}


// ─── Handover Tab ─────────────────────────────────────────────────

function HandoverTab({ items, refresh }: { items: HandoverItem[]; refresh: () => void }) {
  const actionMut = useMutation({
    mutationFn: (v: { item_id: string; status: any; remarks?: string }) =>
      separationApi.actionHandoverItem(v.item_id, { status: v.status, remarks: v.remarks }),
    onSuccess: () => { message.success('Saved'); refresh() },
    onError: (e: any) => message.error(e?.response?.data?.detail ?? 'Save failed'),
  })

  if (items.length === 0) {
    return <Alert type="info" message="No handover items yet. Click 'Generate Checklists' on the header." />
  }

  // Group by handover_type for readability
  const byType: Record<string, HandoverItem[]> = {}
  for (const it of items) {
    if (!byType[it.handover_type]) byType[it.handover_type] = []
    byType[it.handover_type].push(it)
  }

  return (
    <div>
      {Object.entries(byType).map(([type, group]) => (
        <Card key={type} size="small" title={type.replace(/_/g, ' ')}
          style={{ marginBottom: 12 }}>
          {group.map((it) => (
            <Row key={it.id} gutter={8} style={{ marginBottom: 8 }} align="middle">
              <Col flex="auto">
                <Space direction="vertical" size={0}>
                  <Text>{it.item_description}</Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Owner: {it.owner_role ?? '—'}
                    {it.required && <Tag color="red" style={{ marginLeft: 6, fontSize: 10 }}>required</Tag>}
                  </Text>
                </Space>
              </Col>
              <Col>
                <Select
                  size="small"
                  value={it.status}
                  style={{ width: 160 }}
                  onChange={(v) => actionMut.mutate({ item_id: it.id, status: v })}
                  options={[
                    { label: 'PENDING', value: 'PENDING' },
                    { label: 'COMPLETED', value: 'COMPLETED' },
                    { label: 'NOT_APPLICABLE', value: 'NOT_APPLICABLE' },
                    { label: 'WAIVED', value: 'WAIVED' },
                  ]}
                />
              </Col>
            </Row>
          ))}
        </Card>
      ))}
    </div>
  )
}


// ─── Clearance / No-Dues Tab ──────────────────────────────────────

function ClearanceTab({ items, refresh }: { items: ClearanceItem[]; refresh: () => void }) {
  const [editing, setEditing] = useState<ClearanceItem | null>(null)
  const [form] = Form.useForm()

  const actionMut = useMutation({
    mutationFn: (v: any) => separationApi.actionClearanceItem(v.item_id, {
      clearance_status: v.clearance_status,
      amount_recoverable: v.amount_recoverable,
      amount_refundable: v.amount_refundable,
      remarks: v.remarks,
    }),
    onSuccess: () => { message.success('Saved'); refresh(); setEditing(null) },
    onError: (e: any) => message.error(e?.response?.data?.detail ?? 'Save failed'),
  })

  if (items.length === 0) {
    return <Alert type="info" message="No clearance items yet. Click 'Generate Checklists' on the header." />
  }

  // Group by department
  const byDept: Record<string, ClearanceItem[]> = {}
  for (const it of items) {
    if (!byDept[it.clearance_department]) byDept[it.clearance_department] = []
    byDept[it.clearance_department].push(it)
  }

  return (
    <div>
      {Object.entries(byDept).map(([dept, group]) => {
        const pending = group.filter(
          (it) => it.required && (it.clearance_status === 'PENDING' || it.clearance_status === 'HOLD'),
        ).length
        const totalReq = group.filter((it) => it.required).length
        return (
          <Card key={dept} size="small"
            title={
              <Space>
                <Text strong>{dept.replace(/_/g, ' ')}</Text>
                <Tag color={pending === 0 ? 'success' : 'warning'}>
                  {totalReq - pending}/{totalReq} cleared
                </Tag>
              </Space>
            }
            style={{ marginBottom: 12 }}>
            {group.map((it) => (
              <Row key={it.id} gutter={8} style={{ marginBottom: 8 }} align="middle">
                <Col flex="auto">
                  <Space direction="vertical" size={0}>
                    <Text>{it.clearance_item}</Text>
                    <Space size={6}>
                      {it.required && <Tag color="red" style={{ fontSize: 10 }}>required</Tag>}
                      {it.amount_recoverable && it.amount_recoverable > 0 && (
                        <Tag color="orange">Recovery: ₹{it.amount_recoverable.toFixed(2)}</Tag>
                      )}
                      {it.amount_refundable && it.amount_refundable > 0 && (
                        <Tag color="green">Refund: ₹{it.amount_refundable.toFixed(2)}</Tag>
                      )}
                      {it.remarks && <Text type="secondary" style={{ fontSize: 11 }}>{it.remarks}</Text>}
                    </Space>
                  </Space>
                </Col>
                <Col>
                  <Space size={4}>
                    <Tag color={it.clearance_status === 'CLEARED' ? 'success'
                      : it.clearance_status === 'RECOVERY_REQUIRED' ? 'warning'
                      : it.clearance_status === 'HOLD' ? 'red'
                      : it.clearance_status === 'NOT_APPLICABLE' ? 'default'
                      : 'processing'}>
                      {it.clearance_status}
                    </Tag>
                    <Button size="small" icon={<EditOutlined />}
                      onClick={() => {
                        setEditing(it)
                        form.setFieldsValue({
                          clearance_status: it.clearance_status,
                          amount_recoverable: it.amount_recoverable,
                          amount_refundable: it.amount_refundable,
                          remarks: it.remarks,
                        })
                      }} />
                  </Space>
                </Col>
              </Row>
            ))}
          </Card>
        )
      })}

      <Modal
        open={!!editing}
        title={editing ? `${editing.clearance_department} — ${editing.clearance_item}` : ''}
        onCancel={() => setEditing(null)}
        onOk={() => form.validateFields().then((v) =>
          actionMut.mutate({ item_id: editing!.id, ...v })
        )}
        confirmLoading={actionMut.isPending}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="clearance_status" label="Status" rules={[{ required: true }]}>
            <Select options={[
              { label: 'PENDING', value: 'PENDING' },
              { label: 'CLEARED (nothing owed)', value: 'CLEARED' },
              { label: 'RECOVERY REQUIRED', value: 'RECOVERY_REQUIRED' },
              { label: 'HOLD', value: 'HOLD' },
              { label: 'NOT APPLICABLE', value: 'NOT_APPLICABLE' },
            ]} />
          </Form.Item>
          <Form.Item name="amount_recoverable" label="Amount to recover from employee (₹)"
            tooltip="Will deduct from F&F net payable">
            <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="amount_refundable" label="Amount to refund to employee (₹)"
            tooltip="Will add to F&F gross — e.g. security deposit, advance balance">
            <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="remarks" label="Remarks">
            <TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}


// ─── F&F Tab ───────────────────────────────────────────────────────

function FNFTab({ fnf, separationId, canCalculate, refresh }:
  { fnf: any; separationId: string; canCalculate: boolean; refresh: () => void }) {
  const [payRef, setPayRef] = useState('')
  const [payOpen, setPayOpen] = useState(false)

  const calcMut = useMutation({
    mutationFn: () => separationApi.fnfCalculate(separationId),
    onSuccess: () => { message.success('F&F calculated'); refresh() },
    onError: (e: any) => message.error(e?.response?.data?.detail ?? 'Calc failed'),
  })

  const approveMut = useMutation({
    mutationFn: (role: 'HR' | 'ACCOUNTS' | 'MANAGEMENT') =>
      separationApi.fnfApprove(separationId, role),
    onSuccess: () => { message.success('Approved'); refresh() },
    onError: (e: any) => message.error(e?.response?.data?.detail ?? 'Approve failed'),
  })

  const payMut = useMutation({
    mutationFn: () => separationApi.fnfPay(separationId, payRef),
    onSuccess: () => { message.success('Marked PAID'); setPayOpen(false); setPayRef(''); refresh() },
    onError: (e: any) => message.error(e?.response?.data?.detail ?? 'Pay failed'),
  })

  if (!fnf) {
    return (
      <Alert
        type="info" showIcon
        message="No F&F calculation yet"
        description={
          canCalculate
            ? "Click Calculate to generate the draft from current data."
            : "Approve the Last Working Date first — F&F needs it as input."
        }
        action={canCalculate && (
          <Button type="primary" loading={calcMut.isPending}
            onClick={() => calcMut.mutate()}>
            Calculate F&F
          </Button>
        )}
      />
    )
  }

  return (
    <div>
      <Card size="small" style={{ marginBottom: 12 }}>
        <Row gutter={16}>
          <Col xs={24} md={8}>
            <Statistic title="Gross Payable" value={`₹${fnf.gross_payable.toFixed(2)}`}
              valueStyle={{ color: '#52c41a' }} />
          </Col>
          <Col xs={24} md={8}>
            <Statistic title="Total Deductions" value={`₹${fnf.total_deductions.toFixed(2)}`}
              valueStyle={{ color: '#cf1322' }} />
          </Col>
          <Col xs={24} md={8}>
            <Statistic title={fnf.is_negative_recovery ? 'Net (Employee Owes)' : 'Net Payable'}
              value={`₹${Math.abs(fnf.net_payable).toFixed(2)}`}
              valueStyle={{ color: fnf.is_negative_recovery ? '#cf1322' : '#1677ff', fontSize: 28 }} />
          </Col>
        </Row>
      </Card>

      {fnf.review_flags.length > 0 && (
        <Alert
          type="warning" showIcon
          icon={<ExclamationCircleOutlined />}
          style={{ marginBottom: 12 }}
          message={`${fnf.review_flags.length} item(s) flagged for manual review`}
          description={
            <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
              {fnf.review_flags.map((f: any, i: number) =>
                <li key={i}><Text code>{f.field}</Text> — {f.reason}</li>
              )}
            </ul>
          }
        />
      )}

      <DuesPreviewCard separationId={separationId} fnfStatus={fnf.settlement_status}
        onRecalc={() => calcMut.mutate()} recalcing={calcMut.isPending} />

      <Card size="small" title="Earnings" style={{ marginBottom: 12 }}>
        <Descriptions size="small" column={2}>
          <Descriptions.Item label="Salary Days Payable">{fnf.salary_days_payable}</Descriptions.Item>
          <Descriptions.Item label="Basic Payable">₹{fnf.basic_payable.toFixed(2)}</Descriptions.Item>
          <Descriptions.Item label="Allowances">₹{fnf.allowances_payable.toFixed(2)}</Descriptions.Item>
          <Descriptions.Item label="Overtime">₹{fnf.overtime_payable.toFixed(2)}</Descriptions.Item>
          <Descriptions.Item label="Leave Encashment">₹{fnf.leave_encashment_amount.toFixed(2)}</Descriptions.Item>
          <Descriptions.Item label="Bonus">₹{fnf.bonus_payable.toFixed(2)}</Descriptions.Item>
          <Descriptions.Item label="Gratuity">₹{fnf.gratuity_payable.toFixed(2)}</Descriptions.Item>
          <Descriptions.Item label="Incentive">₹{fnf.incentive_payable.toFixed(2)}</Descriptions.Item>
          <Descriptions.Item label="Arrears">₹{fnf.arrears_payable.toFixed(2)}</Descriptions.Item>
          <Descriptions.Item label="Other (incl. refunds)">₹{fnf.other_earnings.toFixed(2)}</Descriptions.Item>
        </Descriptions>
      </Card>

      <Card size="small" title="Deductions / Recoveries" style={{ marginBottom: 12 }}>
        <Descriptions size="small" column={2}>
          <Descriptions.Item label="Notice Recovery">₹{fnf.notice_recovery.toFixed(2)}</Descriptions.Item>
          <Descriptions.Item label="Loan Recovery">₹{fnf.loan_recovery.toFixed(2)}</Descriptions.Item>
          <Descriptions.Item label="Advance Recovery">₹{fnf.advance_recovery.toFixed(2)}</Descriptions.Item>
          <Descriptions.Item label="Canteen Recovery">₹{fnf.canteen_recovery.toFixed(2)}</Descriptions.Item>
          <Descriptions.Item label="Room Rent">₹{fnf.room_rent_recovery.toFixed(2)}</Descriptions.Item>
          <Descriptions.Item label="Room Damage">₹{fnf.room_damage_recovery.toFixed(2)}</Descriptions.Item>
          <Descriptions.Item label="Ration Adjustment">₹{fnf.ration_adjustment.toFixed(2)}</Descriptions.Item>
          <Descriptions.Item label="Asset Damage">₹{fnf.asset_damage_recovery.toFixed(2)}</Descriptions.Item>
          <Descriptions.Item label="Other Deductions">₹{fnf.other_deductions.toFixed(2)}</Descriptions.Item>
        </Descriptions>
      </Card>

      <Card size="small" title="Approval Chain">
        <Space wrap>
          <Button type={fnf.reviewed_by_hr_at ? 'default' : 'primary'}
            disabled={!!fnf.reviewed_by_hr_at || fnf.settlement_status === 'PAID'}
            loading={approveMut.isPending}
            onClick={() => approveMut.mutate('HR')}>
            HR Review
            {fnf.reviewed_by_hr_at && <CheckCircleOutlined style={{ color: '#52c41a', marginLeft: 6 }} />}
          </Button>
          <Button type={fnf.reviewed_by_accounts_at ? 'default' : 'primary'}
            disabled={!fnf.reviewed_by_hr_at || !!fnf.reviewed_by_accounts_at || fnf.settlement_status === 'PAID'}
            loading={approveMut.isPending}
            onClick={() => approveMut.mutate('ACCOUNTS')}>
            Accounts Review
            {fnf.reviewed_by_accounts_at && <CheckCircleOutlined style={{ color: '#52c41a', marginLeft: 6 }} />}
          </Button>
          <Button type={fnf.approved_by_management_at ? 'default' : 'primary'}
            disabled={!fnf.reviewed_by_accounts_at || !!fnf.approved_by_management_at || fnf.settlement_status === 'PAID'}
            loading={approveMut.isPending}
            onClick={() => approveMut.mutate('MANAGEMENT')}>
            Management Approve
            {fnf.approved_by_management_at && <CheckCircleOutlined style={{ color: '#52c41a', marginLeft: 6 }} />}
          </Button>
          <Button danger
            disabled={fnf.settlement_status !== 'APPROVED'}
            onClick={() => setPayOpen(true)}>
            Mark Paid
          </Button>
          <Button onClick={() => calcMut.mutate()}
            disabled={['PAID', 'CLOSED', 'APPROVED', 'PENDING_APPROVAL'].includes(fnf.settlement_status)}
            loading={calcMut.isPending}>
            Recalculate
          </Button>
        </Space>
        {fnf.paid_at && (
          <Alert
            type="success" showIcon style={{ marginTop: 12 }}
            message={`Paid on ${dayjs(fnf.paid_at).format('DD MMM YYYY HH:mm')}`}
            description={`Payment reference: ${fnf.payment_reference}`}
          />
        )}
      </Card>

      <Modal
        open={payOpen}
        title="Record F&F payment"
        onCancel={() => setPayOpen(false)}
        onOk={() => {
          if (!payRef.trim()) return message.warning('Payment reference required')
          payMut.mutate()
        }}
        confirmLoading={payMut.isPending}
        okText="Mark Paid"
      >
        <Form layout="vertical">
          <Form.Item label="Payment Reference"
            help="Cheque no. / NEFT UTR / bank txn ID — for audit">
            <Input value={payRef} onChange={(e) => setPayRef(e.target.value)}
              placeholder="e.g. NEFT-2026-05-25-001234" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}


// ─── Dues Preview Card (Phase 2 — auto-fetched recoveries) ──────

function DuesPreviewCard({
  separationId, fnfStatus, onRecalc, recalcing,
}: {
  separationId: string
  fnfStatus: string
  onRecalc: () => void
  recalcing: boolean
}) {
  const [expanded, setExpanded] = useState(false)
  const previewQ = useQuery({
    queryKey: ['separation-dues-preview', separationId],
    queryFn: () => separationApi.duesPreview(separationId),
    // Don't auto-fetch — only when card is expanded, to keep the F&F tab
    // snappy when HR is just viewing the calculation.
    enabled: expanded,
  })

  const isLocked = ['PAID', 'CLOSED', 'APPROVED', 'PENDING_APPROVAL'].includes(fnfStatus)

  return (
    <Card
      size="small"
      title={
        <Space>
          <Text strong>Auto-Fetched Dues</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            (from advance / ration / payroll / room ledgers)
          </Text>
        </Space>
      }
      extra={
        <Space>
          <Button size="small" onClick={() => setExpanded(!expanded)}>
            {expanded ? 'Hide details' : 'Show details'}
          </Button>
          {!isLocked && (
            <Tooltip title="Re-fetch from ledgers + recalculate F&F">
              <Button size="small" type="primary"
                loading={recalcing}
                onClick={onRecalc}>
                Refresh Dues + Recalc
              </Button>
            </Tooltip>
          )}
        </Space>
      }
      style={{ marginBottom: 12 }}
    >
      <Alert
        type="info" showIcon
        message={
          <span>
            F&F automatically pulls outstanding amounts from existing ledgers when you click <b>Calculate</b>. Click <b>Show details</b> below to see what was found. Manual entries on the No-Dues tab take precedence when higher.
          </span>
        }
      />

      {expanded && previewQ.isLoading && (
        <div style={{ textAlign: 'center', padding: 20 }}><Spin /></div>
      )}

      {expanded && previewQ.data && (
        <div style={{ marginTop: 12 }}>
          <Row gutter={12}>
            {Object.entries(previewQ.data.facilities).map(([key, fac]) => {
              const f = fac as FacilityDues
              const label = DATA_SOURCE_LABEL[f.data_source_status]
              return (
                <Col xs={24} sm={12} md={8} key={key} style={{ marginBottom: 8 }}>
                  <Card size="small" type="inner">
                    <Space direction="vertical" size={4} style={{ width: '100%' }}>
                      <Space style={{ justifyContent: 'space-between', width: '100%' }}>
                        <Text strong>{FACILITY_LABEL[key as keyof typeof FACILITY_LABEL]}</Text>
                        <Tag color={label.color} style={{ fontSize: 10 }}>{label.text}</Tag>
                      </Space>
                      <Text style={{
                        fontSize: 20,
                        color: f.amount > 0 ? '#cf1322' : '#8c8c8c',
                      }}>
                        ₹{f.amount.toFixed(2)}
                      </Text>
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        {f.records_found} record(s)
                      </Text>
                      {f.notes && (
                        <Text type="secondary" style={{ fontSize: 11, fontStyle: 'italic' }}>
                          {f.notes}
                        </Text>
                      )}
                    </Space>
                  </Card>
                </Col>
              )
            })}
          </Row>
          <Alert
            type="warning" showIcon
            style={{ marginTop: 8 }}
            message={
              <span>
                <b>Canteen + Asset/Damage</b> need manual entry on the No-Dues tab — those ledgers aren't yet wired.
                <b> Salary advance</b> matching is by employee name (best-effort) until a proper loan ledger exists.
              </span>
            }
          />
        </div>
      )}
    </Card>
  )
}


// ─── Exit Interview Tab ──────────────────────────────────────────

function ExitInterviewTab({ detail, refresh }: { detail: any; refresh: () => void }) {
  const r = detail.request
  const [form] = Form.useForm()
  const initial = r.exit_interview_data ?? {}

  const saveMut = useMutation({
    mutationFn: (v: any) => separationApi.saveExitInterview(r.id, v),
    onSuccess: () => { message.success('Exit interview saved'); refresh() },
    onError: (e: any) => message.error(e?.response?.data?.detail ?? 'Save failed'),
  })

  return (
    <Form form={form} layout="vertical" initialValues={initial}
      onFinish={(v) => saveMut.mutate(v)}>
      <Row gutter={16}>
        <Col xs={24} md={12}>
          <Form.Item name="reason_for_leaving" label="Primary reason for leaving">
            <Select allowClear options={[
              'Better opportunity', 'Salary issue', 'Personal reason', 'Family reason',
              'Health issue', 'Relocation', 'Higher studies', 'Work pressure',
              'Supervisor issue', 'Shift issue', 'Accommodation issue', 'Transport issue',
              'Retirement', 'Other',
            ].map((r) => ({ label: r, value: r }))} />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item name="rehire_eligible" label="Rehire eligible?">
            <Select options={[
              { label: 'Yes — would rehire', value: 'YES' },
              { label: 'Maybe — with caveats', value: 'MAYBE' },
              { label: 'No', value: 'NO' },
            ]} />
          </Form.Item>
        </Col>
        <Col span={24}><Title level={5} style={{ marginTop: 12 }}>Concerns (rate 1-5)</Title></Col>
        {[
          ['salary_issue', 'Salary / benefits'],
          ['supervisor_issue', 'Supervisor / management'],
          ['work_pressure', 'Work pressure'],
          ['shift_issue', 'Shift / timing'],
          ['accommodation_issue', 'Accommodation'],
          ['canteen_issue', 'Canteen / food'],
          ['transport_issue', 'Transport'],
          ['safety_issue', 'Safety'],
          ['growth_issue', 'Growth / career'],
        ].map(([key, label]) => (
          <Col xs={12} md={8} key={key}>
            <Form.Item name={key} label={label}>
              <InputNumber min={1} max={5} style={{ width: '100%' }}
                placeholder="1 (worst) — 5 (best)" />
            </Form.Item>
          </Col>
        ))}
        <Col span={24}>
          <Form.Item name="suggestions" label="Suggestions to improve">
            <TextArea rows={3} />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item name="hr_remarks" label="HR Remarks">
            <TextArea rows={2} />
          </Form.Item>
        </Col>
      </Row>
      <Space>
        <Button type="primary" htmlType="submit" loading={saveMut.isPending}>
          Save Exit Interview
        </Button>
        {r.exit_interview_status === 'COMPLETED' && (
          <Tag color="success" icon={<CheckCircleOutlined />}>Completed</Tag>
        )}
      </Space>
    </Form>
  )
}
